import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Users, X, ArrowRight, Gift, DollarSign, 
  Share2, Twitter, Linkedin, Copy, Check,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * AffiliateBanner - In-app banner promoting the affiliate program
 * Shows for Growth tier users who haven't joined yet
 * 
 * Usage:
 * <AffiliateBanner user={user} />
 */
export function AffiliateBanner({ user, onDismiss }) {
  // Check localStorage for dismissal - use initial state instead of effect
  const [dismissed, setDismissed] = useState(() => {
    const isDismissed = localStorage.getItem('affiliate_banner_dismissed');
    if (isDismissed) {
      const dismissedTime = parseInt(isDismissed);
      // Show again after 7 days
      return Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000;
    }
    return false;
  });
  
  // Only show for Growth tier users
  if (!user || !['growth', 'teams'].includes(user.tier)) {
    return null;
  }
  
  // Check if user already has affiliate codes
  if (user.has_affiliate_codes) {
    return null;
  }
  
  if (dismissed) {
    return null;
  }
  
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('affiliate_banner_dismissed', Date.now().toString());
    onDismiss?.();
  };
  
  return (
    <div 
      className="relative overflow-hidden bg-gradient-to-r from-indigo-500/10 to-indigo-500/10 border border-indigo-500/30 rounded-xl p-4 mb-6"
      data-testid="affiliate-banner"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <div className="relative flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-6 h-6 text-indigo-400" />
          </div>
          
          <div>
            <h3 className="text-indigo-500 font-semibold flex items-center gap-2">
              Earn 20% recurring commission
              <span className="text-xs bg-indigo-500/20 text-slate-400 px-2 py-0.5 rounded-full">New</span>
            </h3>
            <p className="text-indigo-400 text-sm">
              Love ClientKit? Refer other freelancers and earn money every month.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Link to="/affiliate">
            <Button 
              className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
              data-testid="affiliate-banner-cta"
            >
              <Gift className="w-4 h-4 mr-2" />
              Start Earning
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-indigo-500 transition-colors p-1"
            aria-label="Dismiss banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * AffiliateShareWidget - Social sharing buttons with pre-made copy
 * 
 * Usage:
 * <AffiliateShareWidget referralLink="https://clientkit.app/?ref=john-smith-abc123" />
 */
export function AffiliateShareWidget({ referralLink, userName }) {
  const [copied, setCopied] = useState(null);
  
  const shareMessages = {
    twitter: `I use ClientKit to manage my freelance clients - proposals, contracts, and client communications all in one place. Get 1 month free with my link: ${referralLink}`,
    linkedin: `Fellow freelancers: I've been using ClientKit as my "Freelance OS" and it's been a game-changer. From handling difficult clients to professional proposals, it has everything you need.\n\nGet 1 month free with my referral link: ${referralLink}`,
    email: {
      subject: "Check out ClientKit - 1 month free for you",
      body: `Hey!\n\nI wanted to share this tool I've been using called ClientKit. It's basically everything a freelancer needs - client management, proposals, contracts, and even AI-powered replies for difficult client situations.\n\nI have a referral link that gives you 1 month of the Growth plan free: ${referralLink}\n\nDefinitely worth checking out if you're looking to level up your freelance game!\n\nBest,\n${userName || 'Your friend'}`
    }
  };
  
  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(null), 2000);
    } catch (e) {
      toast.error("Failed to copy");
    }
  };
  
  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessages.twitter)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const openLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const openEmail = () => {
    const url = `mailto:?subject=${encodeURIComponent(shareMessages.email.subject)}&body=${encodeURIComponent(shareMessages.email.body)}`;
    window.location.href = url;
  };
  
  return (
    <div className="space-y-4" data-testid="affiliate-share-widget">
      {/* Quick Share Buttons */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={openTwitter}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 text-slate-400"
        >
          <Twitter className="w-4 h-4 mr-2 text-indigo-400" />
          Share on X
        </Button>
        
        <Button
          onClick={openLinkedIn}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 text-slate-400"
        >
          <Linkedin className="w-4 h-4 mr-2 text-indigo-400" />
          Share on LinkedIn
        </Button>
        
        <Button
          onClick={openEmail}
          variant="outline"
          className="border-slate-700 hover:bg-slate-800 text-slate-400"
        >
          <Share2 className="w-4 h-4 mr-2 text-green-400" />
          Share via Email
        </Button>
      </div>
      
      {/* Pre-made Messages */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-400">Pre-written messages (click to copy)</h4>
        
        {/* Twitter Copy */}
        <div 
          onClick={() => handleCopy(shareMessages.twitter, 'twitter')}
          className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 cursor-pointer hover:border-slate-600 transition-colors group"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
              <Twitter className="w-3 h-3 text-indigo-400" />
              <span>X / Twitter</span>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 px-2 text-xs text-slate-400 group-hover:text-indigo-500"
            >
              {copied === 'twitter' ? (
                <><Check className="w-3 h-3 mr-1" /> Copied</>
              ) : (
                <><Copy className="w-3 h-3 mr-1" /> Copy</>
              )}
            </Button>
          </div>
          <p className="text-sm text-indigo-400 line-clamp-2">{shareMessages.twitter}</p>
        </div>
        
        {/* LinkedIn Copy */}
        <div 
          onClick={() => handleCopy(shareMessages.linkedin, 'linkedin')}
          className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 cursor-pointer hover:border-slate-600 transition-colors group"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
              <Linkedin className="w-3 h-3 text-indigo-400" />
              <span>LinkedIn</span>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 px-2 text-xs text-slate-400 group-hover:text-indigo-500"
            >
              {copied === 'linkedin' ? (
                <><Check className="w-3 h-3 mr-1" /> Copied</>
              ) : (
                <><Copy className="w-3 h-3 mr-1" /> Copy</>
              )}
            </Button>
          </div>
          <p className="text-sm text-indigo-400 line-clamp-2">{shareMessages.linkedin}</p>
        </div>
        
        {/* Link Only */}
        <div 
          onClick={() => handleCopy(referralLink, 'link')}
          className="p-3 bg-indigo-500/10 rounded-lg border border-indigo-500/30 cursor-pointer hover:border-slate-400/50 transition-colors group"
        >
          <div className="flex items-center justify-between">
            <code className="text-sm text-slate-400 truncate flex-1">{referralLink}</code>
            <Button 
              size="sm" 
              className="ml-3 bg-indigo-500 hover:bg-slate-600"
            >
              {copied === 'link' ? (
                <><Check className="w-4 h-4 mr-1" /> Copied</>
              ) : (
                <><Copy className="w-4 h-4 mr-1" /> Copy Link</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * MiniAffiliateBadge - Small badge to show in sidebar or header
 */
export function MiniAffiliateBadge({ user }) {
  if (!user || !['growth', 'teams'].includes(user.tier)) {
    return null;
  }
  
  return (
    <Link 
      to="/affiliate"
      className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 hover:bg-slate-400/20 border border-indigo-500/30 rounded-full transition-colors"
      data-testid="mini-affiliate-badge"
    >
      <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
      <span className="text-xs font-medium text-slate-400">Earn 20%</span>
    </Link>
  );
}
